import React, { useState, useEffect } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import {
  FiHome, FiFileText, FiCheckSquare, FiUsers,
  FiShield, FiSettings, FiChevronLeft, FiChevronRight, FiX,
  FiSliders, FiGrid, FiFeather, FiFolder, FiCreditCard,
  FiChevronDown, FiUserCheck, FiGitBranch, FiLayers,
  FiBriefcase, FiTag, FiLock, FiDatabase, FiCopy, FiDollarSign, FiPaperclip,
  FiRepeat, FiBookOpen, FiBarChart2
} from 'react-icons/fi';
import { RiRobot2Line } from 'react-icons/ri';
import { canAccessRoute, hasPermission } from '../../utils/permissions';
import { TEMPLATE_LIBRARY_ENABLED } from '../../config/features';
import { fetchMyTasks, selectPendingTaskCount } from '../../store/slices/tasksSlice';
import AssistantBot from '../AssistantBot';
import './Sidebar.scss';

// Tab a page lands on when its URL carries no ?tab=, so a deep-link in the
// sidebar still highlights while sitting on the bare path.
const DEFAULT_TAB_BY_PATH = {
  '/hr': 'users',
  '/admin': 'mainGroups',
};

function Sidebar({ collapsed, onToggle, mobileOpen, onMobileClose }) {
  const { t } = useTranslation();
  const location = useLocation();
  const { user, loading: authLoading, initializing } = useSelector((state) => state.auth);
  const userLoading = authLoading || initializing;
  const userRole = user?.role;
  const [wizardOpen, setWizardOpen] = useState(false);
  const dispatch = useDispatch();
  const pendingTaskCount = useSelector(selectPendingTaskCount);
  // Which expandable submenus are open, keyed by item id (e.g. { hr: true }).
  const [openMenus, setOpenMenus] = useState({});

  const canViewApprovals = !!user && canAccessRoute(userRole, '/approvals', user?.effectivePermissions || null);

  // ⌘K / Ctrl+K opens Navi from anywhere; the overlay handles it while open.
  useEffect(() => {
    const handler = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        setWizardOpen(true);
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, []);

  const toggleMenu = (id) => setOpenMenus((prev) => ({ ...prev, [id]: !prev[id] }));

  // Links may carry a query string (e.g. /hr?tab=positions) to deep-link into
  // a specific tab of a page. NavLink only matches on pathname, so compute the
  // active state manually for those, comparing the target tab against the
  // current one — falling back to each page's own default tab when none is set.
  const isQueryLinkActive = (basePath, query) => {
    if (location.pathname !== basePath) return false;
    const target = new URLSearchParams(query).get('tab');
    const current = new URLSearchParams(location.search).get('tab') || DEFAULT_TAB_BY_PATH[basePath];
    return target === current;
  };

  // A page opened out of another section says so via router state (e.g. a
  // contract opened from My Tasks sets navSource '/approvals'). That section
  // stays highlighted instead of the path-matched one, so the user's context
  // is preserved while they look at the detail page.
  const navSource = location.state?.navSource;

  // Renders a single navigable leaf (top-level or nested under a submenu).
  const renderLeaf = (item, isChild = false) => {
    const hasBadge = typeof item.badge === 'number' && item.badge > 0;
    const badgeLabel = hasBadge ? (item.badge > 99 ? '99+' : String(item.badge)) : null;
    const [basePath, query] = item.path.split('?');
    return (
      <NavLink
        key={item.path}
        to={item.path}
        onClick={onMobileClose}
        className={({ isActive }) => {
          const active = navSource
            ? basePath === navSource
            : query ? isQueryLinkActive(basePath, query) : isActive;
          return `sidebar__item ${isChild ? 'sidebar__item--child' : ''} ${active ? 'sidebar__item--active' : ''}`;
        }}
        title={collapsed && hasBadge ? `${item.label} (${item.badge})` : collapsed ? item.label : undefined}
      >
        <span className="sidebar__item-icon-wrap">
          <item.icon className="sidebar__item-icon" />
          {collapsed && hasBadge && (
            <span className="sidebar__item-badge sidebar__item-badge--dot" aria-hidden="true" />
          )}
        </span>
        {!collapsed && <span className="sidebar__item-label">{item.label}</span>}
        {!collapsed && hasBadge && (
          <span className="sidebar__item-badge" aria-label={`${item.badge} pending`}>
            {badgeLabel}
          </span>
        )}
      </NavLink>
    );
  };

  // The badge mirrors the Bekleyenler tab: pending step decisions + contracts
  // sent back for edits + open opinion (Görüş) requests. The list lives in
  // the tasks slice — one request shared with the dashboard widget and the
  // approval notifier, which keeps it polled — and a route change re-checks
  // it unless it was fetched moments ago.
  useEffect(() => {
    if (!canViewApprovals) return;
    dispatch(fetchMyTasks());
  }, [canViewApprovals, location.pathname, dispatch]);


  const navGroups = [
    {
      id: 'main',
      label: null, // No section header for the primary group
      items: [
        { path: '/dashboard', icon: FiHome, label: t('nav.dashboard') },
        { path: '/contracts', icon: FiFileText, label: t('nav.contracts') },
        { path: '/documents', icon: FiFolder, label: t('nav.documents', 'Documents') },
        // General Template Library — browse & download standard contract templates.
        ...(TEMPLATE_LIBRARY_ENABLED
          ? [{ path: '/template-library', icon: FiBookOpen, label: t('nav.templateLibrary', 'Contract Library') }]
          : []),
        // Pending Actions (formerly "Approvals") — approver / data-entry step.
        { path: '/approvals', icon: FiCheckSquare, label: t('nav.approvals'), badge: canViewApprovals ? pendingTaskCount : 0 },
        // Activity Report (Faaliyet Raporu) — sits right under Pending Actions.
        { path: '/activity-report', icon: FiBarChart2, label: t('nav.activityReport', 'Activity Report') },
      ],
    },
    {
      id: 'admin',
      // No group header — the four collapsible sections are the structure.
      label: null,
      items: [
        {
          id: 'business-config',
          icon: FiSliders,
          label: t('nav.businessConfig', 'Contract Settings'),
          // Two logical clusters instead of one flat list: "Definitions" are
          // the lookup lists a contract is built from, "Configurations" the
          // machinery around them. Labels come from adminPanel.tabs.* so the
          // sidebar and the panel's own tab strip can never drift apart.
          sections: [
            {
              id: 'definitions',
              label: t('nav.definitions', 'Definitions'),
              // Parametre bazlı yetkilendirme: each definition screen shows
              // only to holders of its own manage permission (`perm`).
              children: [
                { path: '/admin?tab=mainGroups', perm: 'manage_contract_main_groups', icon: FiLayers, label: t('adminPanel.tabs.mainGroups') },
                { path: '/admin?tab=contractTypes', perm: 'manage_contract_types', icon: FiFileText, label: t('adminPanel.tabs.contractTypes') },
                { path: '/admin?tab=counterpartyTypes', perm: 'manage_counterparty_types', icon: FiBriefcase, label: t('adminPanel.tabs.counterpartyTypes') },
                { path: '/admin?tab=counterparties', perm: 'manage_counterparties', icon: FiUsers, label: t('adminPanel.tabs.counterparties') },
                { path: '/admin?tab=currencies', perm: 'manage_currencies', icon: FiDollarSign, label: t('adminPanel.tabs.currencies') },
                { path: '/admin?tab=supportingDocTypes', perm: 'manage_supporting_doc_types', icon: FiPaperclip, label: t('adminPanel.tabs.supportingDocTypes') },
              ],
            },
            {
              id: 'configurations',
              label: t('nav.configurations', 'Configurations'),
              children: [
                { path: '/admin?tab=schemas', perm: 'manage_field_schemas', icon: FiDatabase, label: t('adminPanel.tabs.schemas') },
                // Approval flow designer — define & manage the workflows that
                // drive the Pending Actions inbox.
                { path: '/flow-management', icon: FiGitBranch, label: t('nav.flowManagement', 'Workflows') },
                { path: '/admin?tab=templates', perm: 'manage_contract_templates', icon: FiCopy, label: t('adminPanel.tabs.templates') },
                ...(TEMPLATE_LIBRARY_ENABLED
                  ? [{ path: '/admin?tab=templateLibrary', perm: 'manage_template_library', icon: FiBookOpen, label: t('adminPanel.tabs.templateLibrary') }]
                  : []),
              ],
            },
          ],
        },
        {
          id: 'hr',
          icon: FiUsers,
          label: t('nav.hrPanel', 'Human Resources'),
          children: [
            // The ?tab= links deep-link into the HR panel's tabs. Order follows
            // day-to-day usage: the records touched constantly come first and
            // rarely-edited master data (companies, groups) sits at the bottom.
            { path: '/hr?tab=users', icon: FiUserCheck, label: t('nav.users', 'Users') },
            { path: '/hr?tab=departments', icon: FiLayers, label: t('nav.orgUnits', 'Units') },
            { path: '/hr?tab=positions', icon: FiBriefcase, label: t('nav.positions', 'Positions') },
            { path: '/hr?tab=titles', icon: FiTag, label: t('nav.titles', 'Titles') },
            { path: '/hr?tab=companies', icon: FiHome, label: t('nav.companies', 'Companies') },
            { path: '/hr?tab=groups', icon: FiGrid, label: t('nav.groups', 'User Groups') },
          ],
        },
        {
          id: 'access-compliance',
          icon: FiShield,
          label: t('nav.accessCompliance', 'Access & Compliance'),
          children: [
            { path: '/permissions', icon: FiLock, label: t('nav.permissions', 'Permissions') },
            { path: '/audit', icon: FiShield, label: t('nav.auditLog') },
            { path: '/signature-integrations', icon: FiFeather, label: t('nav.signatureIntegrations', 'Signature Integrations') },
            { path: '/task-reassignment', icon: FiRepeat, label: t('nav.taskReassignment', 'Task Reassignment') },
          ],
        },
        {
          id: 'administration',
          icon: FiSettings,
          label: t('nav.administration', 'Administration'),
          children: [
            { path: '/subscription', icon: FiCreditCard, label: t('nav.subscription', 'Subscription') },
            { path: '/settings', icon: FiSettings, label: t('nav.settings') },
          ],
        },
      ],
    },
  ];

  return (
    <>
      {/* Mobile overlay */}
      {mobileOpen && (
        <div className="sidebar-overlay" onClick={onMobileClose} />
      )}

      <aside className={`sidebar ${collapsed ? 'sidebar--collapsed' : ''} ${mobileOpen ? 'sidebar--mobile-open' : ''}`}>

        {/* Mobile close button */}
        <div className="sidebar__mobile-header mobile-only">
          <button className="sidebar__close-mobile" onClick={onMobileClose}>
            <FiX />
          </button>
        </div>

        {/* Wizard Trigger */}
        <button
          type="button"
          className="sidebar__wizard-trigger"
          onClick={() => setWizardOpen(true)}
          title={collapsed ? t('nav.wizard', 'How can I help?') : undefined}
        >
          <span className="sidebar__wizard-icon">
            <RiRobot2Line />
          </span>
          {!collapsed && (
            <span className="sidebar__wizard-label">
              {t('nav.wizard', 'How can I help?')}
            </span>
          )}
        </button>

        {/* Navigation */}
        <nav className="sidebar__nav">
          {navGroups.map((group) => {
            // Permissions are keyed by pathname — ignore any ?tab= deep-link
            // part. An entry may additionally carry its own `perm` (e.g. the
            // per-parameter Definitions screens), which must also be held.
            const canAccess = (entry) => {
              if (!canAccessRoute(userRole, entry.path.split('?')[0], user?.effectivePermissions || null)) return false;
              if (entry.perm) return hasPermission(userRole, entry.perm, user?.effectivePermissions || null);
              return true;
            };
            // A submenu's leaves may sit directly in `children` or be grouped
            // under labelled `sections` — flatten for visibility checks.
            const leavesOf = (item) =>
              item.children || (item.sections ? item.sections.flatMap((s) => s.children) : null);
            const visibleItems = group.items.filter((item) => {
              const leaves = leavesOf(item);
              return leaves ? leaves.some((c) => canAccess(c)) : canAccess(item);
            });
            if (visibleItems.length === 0) return null;

            return (
              <div key={group.id} className="sidebar__group">
                {!collapsed && group.label && (
                  <span className="sidebar__group-label">{group.label}</span>
                )}
                <div className="sidebar__group-items">
                  {visibleItems.map((item) => {
                    if (!leavesOf(item)) return renderLeaf(item);

                    // Normalise: a plain `children` list is one unlabelled section.
                    const visibleSections = (item.sections || [{ id: item.id, label: null, children: item.children }])
                      .map((s) => ({ ...s, children: s.children.filter((c) => canAccess(c)) }))
                      .filter((s) => s.children.length > 0);
                    const visibleChildren = visibleSections.flatMap((s) => s.children);
                    // Collapsed rail has no room for a submenu — flatten the
                    // children into individual icon links so they stay reachable.
                    // Several children may deep-link into different tabs of the
                    // same page (e.g. /hr?tab=…); keep only one icon per page.
                    if (collapsed) {
                      const seen = new Set();
                      return visibleChildren
                        .filter((c) => {
                          const base = c.path.split('?')[0];
                          if (seen.has(base)) return false;
                          seen.add(base);
                          return true;
                        })
                        .map((c) => renderLeaf(c));
                    }

                    const childActive = visibleChildren.some((c) => location.pathname === c.path.split('?')[0]);
                    // Sections start collapsed; the user toggles them open.
                    const expanded = openMenus[item.id] ?? false;
                    return (
                      <div key={item.id} className="sidebar__submenu">
                        <button
                          type="button"
                          className={`sidebar__item sidebar__item--parent ${childActive ? 'sidebar__item--child-active' : ''}`}
                          onClick={() => toggleMenu(item.id)}
                          aria-expanded={expanded}
                        >
                          <span className="sidebar__item-icon-wrap">
                            <item.icon className="sidebar__item-icon" />
                          </span>
                          <span className="sidebar__item-label">{item.label}</span>
                          <FiChevronDown className={`sidebar__item-chevron ${expanded ? 'is-open' : ''}`} />
                        </button>
                        {expanded && (
                          <div className="sidebar__submenu-items">
                            {visibleSections.map((s) => (
                              <React.Fragment key={s.id}>
                                {s.label && (
                                  <span className="sidebar__submenu-heading">{s.label}</span>
                                )}
                                {s.children.map((c) => renderLeaf(c, true))}
                              </React.Fragment>
                            ))}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </nav>

        {/* Footer */}
        <div className="sidebar__footer">
          <div className="sidebar__user">
            {userLoading ? (
              <>
                <div className="sidebar__user-avatar sidebar__user-avatar--skeleton" />
                {!collapsed && (
                  <div className="sidebar__user-info">
                    <div className="sidebar__skeleton-name" />
                    <div className="sidebar__skeleton-role" />
                  </div>
                )}
              </>
            ) : user ? (
              <>
                <div className="sidebar__user-avatar">
                  {user.firstName?.[0]}{user.lastName?.[0]}
                </div>
                {!collapsed && (
                  <div className="sidebar__user-info">
                    <span className="sidebar__user-name">{user.firstName} {user.lastName}</span>
                    <span className="sidebar__user-role">{user.role?.replace(/_/g, ' ')}</span>
                  </div>
                )}
              </>
            ) : null}
          </div>

          <button
            className="sidebar__toggle desktop-only"
            onClick={onToggle}
            title={collapsed ? t('common.next') : t('common.previous')}
          >
            {collapsed ? <FiChevronRight /> : <FiChevronLeft />}
          </button>
        </div>
      </aside>

      <AssistantBot open={wizardOpen} onClose={() => setWizardOpen(false)} />
    </>
  );
}

export default Sidebar;
